import { SxProps, Theme, styled, useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import moment from "moment";
import { Link } from "react-router-dom";

import {
  ArrowRightIcon,
  getAppConsistentParams,
  getProductUrl,
  getRetailerFromId,
  hexadecimal,
} from "@brandclub/common-ui";
import KeyboardArrowRightRounded from "@mui/icons-material/KeyboardArrowRightRounded";
import { ComponentProps, FC, PropsWithChildren } from "react";
import { MOBILE_SCREEN_SIZE } from "../../../../AppNavigation/constants";
import { StyledLink } from "../../../../AppNavigation/StyledLink";
import { useCheckoutDrawer } from "../../../../Checkout/useCheckoutDrawer";
import { isItemCheckoutSuccess } from "../../../../Checkout/utils";

import {
  SolidButton,
  OutlineButton,
} from "../../../../StoreComponents/StoreButton";
import { PurchaseRowType } from "./data";
import ProductImageWithQuantity from "./ProductImageWithQuantity";
import { getShipmentStatus } from "./purchaseUtil";
import { useAppSelector, useAppDispatch } from "../../../../../redux/hooks";
import { addOptimisticShoppingCartItem } from "@/redux/reducers/checkout";
import {
  UserRetailerCheckoutOrder,
  UserRetailerCheckoutOrderStatusV2,
} from "@/components/Checkout/types";

const getDetailLink = (order: UserRetailerCheckoutOrder) => {
  const status = order.status;
  if (status === UserRetailerCheckoutOrderStatusV2.checkout_in_progress) {
    return `/checkoutSummary?orderId=${order.orderId}`;
  }
  return `/dashboard/purchase/${order.orderId}`;
};

const SvgContainer = styled("svg")(({ theme }) => ({
  width: "20px",
  height: "20px",
  [`@media screen and (max-width:${MOBILE_SCREEN_SIZE}px)`]: {
    width: "14px",
    height: "14px",
  },
}));

const ErrorIcon = () => {
  return (
    <SvgContainer viewBox="0 0 12 12">
      <g data-name="Group 21887">
        <path
          data-name="Path 15781"
          d="M6 0a6 6 0 1 1-6 6 6 6 0 0 1 6-6z"
          style={{ fill: "#ff534a" }}
        />
        <g data-name="cancel/close">
          <g data-name="Group 418">
            <path
              data-name="Path 435"
              d="M2.362 2.116 4.175.3a.176.176 0 1 0-.244-.25L2.113 1.867.3.054A.176.176 0 0 0 .051.3l1.813 1.816L.051 3.929a.176.176 0 0 0 .249.249l1.813-1.813 1.813 1.813a.176.176 0 0 0 .249-.249z"
              transform="translate(3.884 3.885)"
              style={{ fill: "#fff", stroke: "#fff", strokeWidth: ".6px" }}
            />
          </g>
        </g>
      </g>
    </SvgContainer>
  );
};

const TitleLink = styled("div")(({ theme }) => ({
  margin: "0 0 15px 0",
  position: "relative",

  cursor: "pointer",
  color: theme.palette.primary.main,
  width: "fit-content",
  display: "flex",

  alignItems: "center",
  ".hover_arrow": {
    position: "absolute",
    right: "-26px",
    display: "flex",
    paddingBottom: "2px",

    transition: "opacity 0.3s ease-in-out 0.1s",

    opacity: 0,
    alignItems: "center",
    gap: "4px",
    color: theme.palette.primary.main,
  },
  "&:hover": {
    ".hover_arrow": {
      opacity: 1,
    },
  },

  ":after": {
    content: "''",
    display: "block",
    position: "absolute",
    backgroundColor: theme.palette.primary.main,
    width: 0,
    bottom: "-2px",
    height: "2px",
    transition: "width 0.3s ease-in-out",
    willWhange: "width",
    borderRadius: "10px",
  },
}));

const RowContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",

  alignItems: "flex-start",
  padding: "30px 0",
  ".info": {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: "16px",
    [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      gap: "20px",
    },
    ".detail": {
      display: "flex",
      flexDirection: "column",
      gap: "6px",
      [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
        gap: 4,
      },
      a: {
        textDecoration: "none",
        width: "fit-content",
        "&:hover": {
          div: {
            ":after": {
              width: "100%",
            },
          },
        },
      },
      ".title": {
        whiteSpace: "nowrap",
        fontSize: "16px",
        fontWeight: 700,
        marginBottom: 3,
        [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
          fontSize: 14,
          marginBottom: 0,
        },
      },
      ".detail_row": {
        display: "flex",
        flexDirection: "row",
        gap: "10px",
        ".label": {
          fontSize: "14px",
          fontWeight: 500,
        },
        ".value": {
          fontSize: "14px",
          fontWeight: 600,
        },
      },
    },
  },
  ".actions": {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    alignItems: "flex-end",
    justifyContent: "space-between",
    [`@media screen and (max-width:${MOBILE_SCREEN_SIZE}px)`]: {
      flexDirection: "row",
    },
    a: {
      textDecoration: "none",
    },

    ".link": {
      display: "flex",
      flexDirection: "row",
      gap: "4px",
      alignItems: "center",
      fontSize: "14px",
      fontWeight: 600,
      color: theme.palette.primary.main,
    },
  },
}));

interface ActionButtonProps extends ComponentProps<typeof SolidButton> {
  primary?: boolean;
}
const ActionButton: FC<ActionButtonProps> = ({ primary, sx, ...props }) => {
  if (primary) {
    return (
      <SolidButton
        {...props}
        sx={{
          width: "100%",
          whiteSpace: "nowrap",
          height: 35,
          fontSize: 13,
          [`@media screen and (max-width:${MOBILE_SCREEN_SIZE}px)`]: {
            fontSize: 12,
            height: 27,
            padding: "0 12px",
          },
          ...sx,
        }}
      />
    );
  }
  return (
    <OutlineButton
      {...props}
      sx={{
        width: "100%",
        whiteSpace: "nowrap",
        height: 35,
        fontSize: 13,
        [`@media screen and (max-width:${MOBILE_SCREEN_SIZE}px)`]: {
          fontSize: 12,
          height: 27,
          padding: "0 12px",
        },
        ...sx,
      }}
    />
  );
};

const ProductDetails = (
  product: PurchaseRowType & {
    checkoutOrderStatus: UserRetailerCheckoutOrderStatusV2;
  }
) => {
  const isMobileView = useMediaQuery(`(max-width:${MOBILE_SCREEN_SIZE}px)`);

  return (
    <>
      <div className="detail_row">
        <div className="label">Retailer:</div>
        <div className="value">{getRetailerFromId(product.retailerId)}</div>
      </div>
      {!isMobileView && (
        <div className="detail_row">
          <div className="label">Retailer order:</div>
          <div className="value">
            <a
              href={product.retailerOrderPageUrl}
              rel="noreferrer"
              target="_blank"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              {product.retailerOrderId ? `${product.retailerOrderId}` : "--"}
            </a>
          </div>
        </div>
      )}
      <div className="detail_row">
        <div className="label">Order date:</div>
        <div className="value">
          {moment(product.updatedTime).format("MMM DD, YYYY")}
        </div>
      </div>
      {!isMobileView && (
        <div className="detail_row">
          <div className="label">Status:</div>
          <div className="value">
            {getShipmentStatus(
              product.orderTracking?.shipmentStatus,
              product.checkoutOrderStatus
            )}
          </div>
        </div>
      )}
    </>
  );
};

const PurchaseActionButtons = ({
  item,
  children,
}: PropsWithChildren<{
  item: PurchaseRowType;
}>) => {
  const { openDrawer } = useCheckoutDrawer();
  // const { routePrefix } = useSiteContext();
  // const appConfig = useAppSelector((state) => state.appConfig);
  const dispatch = useAppDispatch();
  const isMobileView = useMediaQuery(`(max-width:${MOBILE_SCREEN_SIZE}px)`);
  // const onDTC = appConfig?.domainConfig.storeBrandingType === "CustomDTCStore";

  // const purchaseDetailPageUrl = onDTC
  //   ? `/purchase/${item.checkoutBatchId}`
  //   : routePrefix
  //   ? `${routePrefix}/branddashboard/purchase/${item.checkoutBatchId}`
  //   : `/dashboard/purchase/${item.checkoutBatchId}`;

  const purchaseDetailPageUrl = getDetailLink(item);

  const buyAgain = async () => {
    dispatch(
      addOptimisticShoppingCartItem({
        items: [
          {
            brandId: item.brandId,
            stacklineSku: item.stacklineSku,
            retailerId: item.retailerId,
            quantity: 1,
            retailerParentSku: item.retailerParentSku,
            retailerSku: item.retailerSku,
            retailPrice: item.retailPrice,
          },
        ],
        retailerId: item.retailerId,
        totalItems: 1,
      })
    );

    openDrawer();
  };

  const canWriteReview = isItemCheckoutSuccess(item) && item.canWriteReview;
  if (
    item.status === UserRetailerCheckoutOrderStatusV2.checkout_in_progress &&
    item?.userInteractionRequirement?.type
  ) {
    return (
      <div className="actions">
        <Box
          sx={{
            gap: "7.2px",
            display: "flex",
            flexDirection: canWriteReview ? "column-reverse" : "column",
            width: "100%",
            [`@media screen and (max-width:${MOBILE_SCREEN_SIZE}px)`]: {
              flexDirection: "row",
              gap: "5.4px",
              width: "auto",
            },
          }}
        >
          <Link to={purchaseDetailPageUrl}>
            <ActionButton
              sx={{
                background: hexadecimal("#ff534a")(30),
                color: "#ff534a",
                border: "none",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
              trackedAdditionalData={{ ...item }}
              primary={isMobileView}
            >
              <ErrorIcon />
              Resolve issues
            </ActionButton>
          </Link>
        </Box>
      </div>
    );
  } else if (
    item.status === UserRetailerCheckoutOrderStatusV2.checkout_error ||
    item.status === UserRetailerCheckoutOrderStatusV2.user_interaction_timeout
  ) {
    return (
      <div className="actions">
        <Box
          sx={{
            gap: "7.2px",
            display: "flex",
            flexDirection: canWriteReview ? "column-reverse" : "column",
            width: "100%",
            [`@media screen and (max-width:${MOBILE_SCREEN_SIZE}px)`]: {
              flexDirection: "row",
              gap: "5.4px",
              width: "auto",
            },
          }}
        >
          <Link to={purchaseDetailPageUrl}>
            <ActionButton
              sx={{
                background: hexadecimal("#ff534a")(30),
                color: "#ff534a",
                border: "none",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
              trackedAdditionalData={{ ...item }}
              primary={isMobileView}
            >
              <ErrorIcon />
              View issues
            </ActionButton>
          </Link>
        </Box>
      </div>
    );
  } else if (
    item.status === UserRetailerCheckoutOrderStatusV2.checkout_in_progress &&
    !item?.userInteractionRequirement?.type
  ) {
    return (
      <div className="actions">
        <Box
          sx={{
            gap: "7.2px",
            display: "flex",
            flexDirection: canWriteReview ? "column-reverse" : "column",
            width: "100%",
            [`@media screen and (max-width:${MOBILE_SCREEN_SIZE}px)`]: {
              flexDirection: "row",
              gap: "5.4px",
              width: "auto",
            },
          }}
        >
          <Link to={purchaseDetailPageUrl}>
            <ActionButton
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
              trackedAdditionalData={{ ...item }}
              primary={isMobileView}
            >
              View purchase
            </ActionButton>
          </Link>
        </Box>
      </div>
    );
  }
  return (
    <div className="actions">
      <Box
        sx={{
          gap: "7.2px",
          display: "flex",
          flexDirection: canWriteReview ? "column-reverse" : "column",
          width: "100%",
          [`@media screen and (max-width:${MOBILE_SCREEN_SIZE}px)`]: {
            flexDirection: "row",
            gap: "5.4px",
            width: "auto",
          },
        }}
      >
        {canWriteReview ? (
          <>
            <ActionButton
              trackedAdditionalData={{ ...item }}
              primary={isMobileView}
              onClick={buyAgain}
            >
              Buy again
            </ActionButton>
            <StyledLink
              to={`/review/${item.stacklineSku}`}
              state={{ product: item }}
              sx={{ textDecoration: "none" }}
            >
              <ActionButton
                trackedAdditionalData={{ ...item }}
                primary={!isMobileView}
              >
                Write review
              </ActionButton>
            </StyledLink>
          </>
        ) : (
          <>
            <ActionButton
              trackedAdditionalData={{ ...item }}
              primary
              onClick={buyAgain}
            >
              Buy again
            </ActionButton>
            <Link to={purchaseDetailPageUrl}>
              <ActionButton
                trackedAdditionalData={{
                  linkTo: purchaseDetailPageUrl,
                  ...item,
                }}
              >
                View purchase
              </ActionButton>
            </Link>
          </>
        )}
      </Box>
      {children}
    </div>
  );
};

const PurchaseListRow = ({
  item,
  containerStyle = {},
  shouldShowTrackPackage = true,
}: {
  item: PurchaseRowType;
  containerStyle?: SxProps<Theme>;
  shouldShowTrackPackage?: boolean;
}) => {
  // const { routePrefix } = useSiteContext();

  const appConfig = useAppSelector((state) => state.appConfig);
  const isMobileView = useMediaQuery(`(max-width:${MOBILE_SCREEN_SIZE}px)`);
  const urlSearchParams = getAppConsistentParams(window.location.search);
  const targetUrl = getProductUrl({
    title: item.title,
    stacklineSku: item.stacklineSku,
    urlSearchParams,
    dtcProductPageUrl: item.dtcProductPageUrl,
    storeType: appConfig?.domainConfig?.storeBrandingType,
  });
  const showTrackPackageLink =
    shouldShowTrackPackage &&
    !isMobileView &&
    item.status !== UserRetailerCheckoutOrderStatusV2.checkout_in_progress &&
    item.status !== UserRetailerCheckoutOrderStatusV2.user_interaction_timeout;

  const purchaseDetailPageUrl = getDetailLink(item);

  return (
    <RowContainer className="row" sx={{ ...containerStyle }}>
      <div className="info">
        <Link
          to={targetUrl}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <ProductImageWithQuantity
            url={item?.imageUrls?.[0]}
            quantity={item.quantity}
          />
        </Link>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            height: "100%",
            flex: 1,
            justifyContent: "space-between",
            [`@media screen and (max-width:${MOBILE_SCREEN_SIZE}px)`]: {
              flex: "unset",
              width: "auto",
              gap: "10px",
              flexDirection: "column",
            },
          }}
        >
          <div className="detail">
            <Link
              to={purchaseDetailPageUrl}
              style={{
                textDecoration: "none",
                color: "inherit",
                cursor: "pointer",

                pointerEvents: "auto",
              }}
            >
              <TitleLink className="title">
                Order #{item.orderId}{" "}
                <div className="hover_arrow" style={{}}>
                  <ArrowRightIcon sx={{ fontSize: 16 }}></ArrowRightIcon>
                </div>
              </TitleLink>
            </Link>
            <ProductDetails
              {...item}
              checkoutOrderStatus={
                item.status as UserRetailerCheckoutOrderStatusV2
              }
            />
          </div>
          <PurchaseActionButtons item={item}>
            {showTrackPackageLink && (
              <a
                href={item.retailerOrderPageUrl}
                rel="noreferrer"
                target="_blank"
                style={{
                  textDecoration: "none",

                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  color: "inherit",
                }}
              >
                <div className="link">Track package</div>
                <KeyboardArrowRightRounded
                  style={{ color: "inherit", fontSize: 21, marginRight: -3 }}
                />
              </a>
            )}
          </PurchaseActionButtons>
        </Box>
      </div>
    </RowContainer>
  );
};

export default PurchaseListRow;
